import { useQuery, type UseQueryResult } from '@tanstack/react-query';

interface BoundConversation {
  readonly conversationId: string;
}

export interface ScopedAssistantConversationOptions {
  readonly queryKey: readonly unknown[];
  readonly enabled: boolean;
  /** The caller's conversations bound to the resource, newest first. */
  readonly list: () => Promise<readonly BoundConversation[]>;
  /** Creates a conversation bound to the resource. */
  readonly create: () => Promise<BoundConversation>;
}

/**
 * The conversation an assistant bound to one resource talks in: the most
 * recent one already bound to it, or a fresh one bound on creation.
 *
 * Mounting is the trigger, so a screen that mounts its chat lazily mints
 * nothing until the chat is shown. Cached forever, so remounting can't mint a
 * second one either.
 */
export function useScopedAssistantConversation({
  queryKey,
  enabled,
  list,
  create,
}: ScopedAssistantConversationOptions): UseQueryResult<string, Error> {
  return useQuery<string, Error>({
    queryKey,
    queryFn: async () => {
      const existing = await list();
      if (existing.length > 0) return existing[0].conversationId;
      const created = await create();
      return created.conversationId;
    },
    enabled,
    // A retry could create a second conversation after a create that actually
    // landed, so failures surface instead.
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
